import { batchMarketPrice } from '@/api/trade';
import {
  applyBatchPriceMapToFeedRows,
  chunkFeedBatchPriceItems,
  collectFeedBatchPriceItems,
  FEED_BATCH_PRICE_CHUNK_SIZE,
  FEED_BATCH_PRICE_MAX_ITEMS,
} from '@/domain/market/feed-batch-price.js';

/** store → 是否有后台 FairOdds 刷新进行中（不阻塞列表） */
const inflightByStore = new WeakMap();

function deferIdle(run) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => run(), { timeout: 2000 });
    return;
  }
  setTimeout(run, 0);
}

/**
 * 后台跑一轮 batchPrice 写回 rawRows；失败静默。
 * 不设 listLoading / loadingMore，不 await 于调用方关键路径。
 */
function isFeedBusy(store) {
  return Boolean(store?.listLoading) || Boolean(store?.loadingMore);
}

async function runFeedFairOddsRefresh(store, options = {}) {
  if (!store || inflightByStore.get(store)) return;
  if (!store.initialized) return;
  if (isFeedBusy(store)) return;

  const rows = store.rawRows;
  if (!Array.isArray(rows) || !rows.length) return;

  const items = collectFeedBatchPriceItems(rows, {
    maxItems: options.maxItems ?? FEED_BATCH_PRICE_MAX_ITEMS,
  });
  if (!items.length) return;

  const gen = Number(store.loadGeneration) || 0;
  inflightByStore.set(store, true);

  try {
    const chunks = chunkFeedBatchPriceItems(
      items,
      options.chunkSize ?? FEED_BATCH_PRICE_CHUNK_SIZE
    );

    for (const chunk of chunks) {
      if ((Number(store.loadGeneration) || 0) !== gen) return;
      if (isFeedBusy(store)) return;

      let batchMap;
      try {
        batchMap = await batchMarketPrice(chunk);
      } catch (err) {
        console.warn('[feed:batchPrice] refresh failed', err);
        return;
      }

      if ((Number(store.loadGeneration) || 0) !== gen) return;
      if (!Array.isArray(store.rawRows) || !store.rawRows.length) return;

      const applied = applyBatchPriceMapToFeedRows(store.rawRows, batchMap);
      if (applied) {
        // 浅拷贝数组触发 presentation computed 重映射；不整表重拉
        store.rawRows = store.rawRows.slice();
      }
    }
  } finally {
    inflightByStore.set(store, false);
  }
}

/**
 * 调度 Feed FairOdds 批量刷新：idle / 下一 macrotask，不阻塞首屏与 fetchPage。
 * @param {object} store 具备 rawRows / loadGeneration / listLoading 的 Pinia feed store
 * @param {object} [options]
 */
export function scheduleFeedFairOddsRefresh(store, options = {}) {
  if (!store) return;
  deferIdle(() => {
    void runFeedFairOddsRefresh(store, options);
  });
}
